import { differenceInCalendarDays } from 'date-fns';
import { ClassNames, DayPicker, Row, RowProps } from 'react-day-picker';
import moment from 'moment-timezone';
import { IEmployee, IBusiness, IService, EmployeeSpecificDateOverride, SpecificDateHours } from '../state/api/types';

const DEFAULT_BUSINESS_TZ = 'America/Montevideo';

/**
 * Get "today" and "now" in the business's timezone.
 * Single source of truth for all business-time calculations.
 */
export const getBusinessNow = (businessTimezone?: string) => {
    const tz = businessTimezone || DEFAULT_BUSINESS_TZ;
    const now = moment().tz(tz);
    return {
        todayStr: now.format('YYYY-MM-DD'),
        nowTimeStr: now.format('HH:mm'),
        todayDate: now.clone().startOf('day').toDate(),
    };
};

/**
 * Convert a Unix timestamp to YYYY-MM-DD in the business timezone.
 * Replaces raw moment.unix(ts).format('YYYY-MM-DD') calls to ensure
 * all date-string comparisons use the same timezone.
 */
export const unixToBusinessDateString = (unixTimestamp: number, businessTimezone?: string): string => {
    const tz = businessTimezone || DEFAULT_BUSINESS_TZ;
    return moment.unix(unixTimestamp).tz(tz).format('YYYY-MM-DD');
};

/**
 * Timezone-aware past-date check using business timezone.
 * Use this instead of isPastDate() when the business object is available.
 */
export function isPastDateForBusiness(date: Date, businessTimezone?: string): boolean {
    const tz = businessTimezone || DEFAULT_BUSINESS_TZ;
    const dateDayStr = moment(date).format('YYYY-MM-DD');
    const todayStr = moment().tz(tz).format('YYYY-MM-DD');
    return dateDayStr < todayStr;
}

export function isPastDate(date: Date) {
    return differenceInCalendarDays(date, new Date()) < 0;
};

export function OnlyFutureRow(props: RowProps) {
    const isPastRow = props.dates.every(isPastDate);
    if (isPastRow) return <></>;
    return <Row {...props} />;
};

export function calculateAverageStarRating(starCount: { [key: string]: number } | undefined): string {
    if (!starCount || Object.keys(starCount).length === 0) {
        return "0.0";
    }

    const totalStars = Object.entries(starCount).reduce((sum, [key, count]) => sum + parseInt(key) * count, 0);
    const totalRatings = Object.values(starCount).reduce((sum, count) => sum + count, 0);

    const average = totalRatings > 0 ? totalStars / totalRatings : 0;

    // Round to one decimal place and always show the decimal
    return (Math.round(average * 10) / 10).toFixed(1);
}

export function calculateTotalStartsFromStarCount(starCount: { [key: string]: number } | undefined): number {
    if (!starCount || Object.keys(starCount).length === 0) {
        return 0;
    }
    return Object.values(starCount).reduce((sum, count) => sum + count, 0);
}

export function calculatePercentageStarCount(starCount: number, totalStars: number): number {
    if (totalStars === 0) {
        return 0;
    }
    return Math.round((starCount / totalStars) * 100);
}

export const unixToDatestring = (unixTimestamp: number, timezone?: string): string => {
    if (timezone) {
        return moment.unix(unixTimestamp).tz(timezone).format('YYYY-MM-DD');
    }
    return moment.unix(unixTimestamp).format('YYYY-MM-DD');
};

/**
 * Checks if a booking's appointment time has already passed
 * Used to filter out past bookings from "upcoming" lists
 * @param booking - Object with booking time data
 * @returns true if the booking time is in the past
 */
export const isBookingExpired = (booking: {
    bookDate?: number;
    startTimestamp?: number;
    selectedTime?: { startTime: string }
}, businessTimezone?: string): boolean => {
    // Use startTimestamp if available (more accurate, timezone-independent)
    if (booking.startTimestamp) {
        return moment.unix(booking.startTimestamp).isBefore(moment());
    }

    // Fallback to bookDate + selectedTime — use business timezone
    if (booking.bookDate && booking.selectedTime?.startTime) {
        const base = businessTimezone
            ? moment.unix(booking.bookDate).tz(businessTimezone)
            : moment.unix(booking.bookDate);
        const bookingDateTime = base.set({
                hour: parseInt(booking.selectedTime.startTime.split(':')[0]),
                minute: parseInt(booking.selectedTime.startTime.split(':')[1]),
                second: 0,
                millisecond: 0
            });
        return bookingDateTime.isBefore(moment());
    }

    // If no time data, consider it expired (safe default)
    return true;
};

export function isTimeOlderThan(time1: string, time2: string): boolean {
    const [hours1, minutes1] = time1.split(':').map(Number);
    const [hours2, minutes2] = time2.split(':').map(Number);

    const totalMinutes1 = hours1 * 60 + minutes1;
    const totalMinutes2 = hours2 * 60 + minutes2;

    return totalMinutes1 < totalMinutes2;
}

export const getEmployeeWorkingHoursForDay = (employee: any, dateString: string): any | undefined => {
    if (!employee || !employee.workingHours || employee.workingHours.length === 0) {
        return undefined;
    }

    // Get the day of the week from moment (0=Sunday, 6=Saturday) and convert to Monday-first format (0=Monday, 6=Sunday)
    const jsDay = moment(dateString).day();
    const dayOfWeek = ((jsDay + 6) % 7).toString();

    // Find the matching working hours for this day
    return employee.workingHours.find((wh: any) => wh.day === dayOfWeek);
};

/**
 * DAY INDEX SCHEMES DOCUMENTATION:
 *
 * JavaScript/Moment: 0=Sunday, 1=Monday, ..., 6=Saturday
 * Business hours array: index 0=Monday, 1=Tuesday, ..., 6=Sunday
 * Employee workingHours.day: "0"=Sunday, "1"=Monday, ..., "6"=Saturday (string, JS scheme)
 *
 * This function converts JS day index to business hours array index.
 */
export const jsDayToBusinessIndex = (jsDay: number): number => {
    return (jsDay + 6) % 7;
};

/**
 * Checks if an employee works on a specific date
 * @param employee - The employee object with workingHours
 * @param date - The date to check
 * @param business - The business object with hours and overrides
 * @returns true if the date should be DISABLED (employee doesn't work)
 */
export const isDateDisabledForEmployee = (
    employee: IEmployee | null,
    date: Date,
    business: IBusiness
): boolean => {
    const dateString = moment(date).format('YYYY-MM-DD');
    const jsDayIndex = moment(date).day(); // 0=Sunday, 6=Saturday

    // 1. Check Business Override (highest priority)
    const businessOverride = business?.specificDatesOverride?.find((oh: SpecificDateHours) => {
        return unixToBusinessDateString(oh.date, business?.timezone) === dateString;
    });

    if (businessOverride && !businessOverride.open) {
        return true; // Business explicitly closed
    }

    // 2. Check Business Regular Hours (if no override)
    if (!businessOverride) {
        const businessDayIndex = jsDayToBusinessIndex(jsDayIndex);
        const dayHours = business?.hours ? business.hours[businessDayIndex] : null;

        if (business?.hours && (!dayHours || dayHours.length === 0)) {
            return true; // Business closed on this day of week
        }
    }

    // 3. If no employee selected, only business rules apply
    if (!employee) {
        return false;
    }

    // 4. Check Employee Override (specific date closure)
    const empOverride = employee.specificDatesOverride?.find((override: EmployeeSpecificDateOverride) => {
        return unixToBusinessDateString(override.date, business?.timezone) === dateString;
    });

    if (empOverride?.isClosed) {
        return true; // Employee explicitly unavailable
    }

    // 5. Check Employee Working Hours (day of week) - convert JS day to Monday-first format
    const empDayStr = ((jsDayIndex + 6) % 7).toString();
    const empHours = employee.workingHours?.find((wh) => wh.day === empDayStr);

    if (!empHours || !empHours.isActive) {
        return true; // Employee doesn't work this day of week
    }

    return false; // Date is available
};

/**
 * Parses a full name string into firstName and lastName
 * @param fullName - The full name string to parse
 * @returns Object with firstName and lastName
 */
export const parseFullName = (fullName: string): { firstName: string; lastName: string } => {
    const parts = fullName.trim().split(' ');
    return {
        firstName: parts[0] || '',
        lastName: parts.slice(1).join(' ') || ''
    };
};

export const isVariableService = (service: IService): boolean =>
    service.pricing_type === 'VARIABLE';

export const formatServicePrice = (service: IService): string => {
    if (isVariableService(service)) return 'A consultar';
    if (service.price == null) return 'A consultar';
    return `$ ${service.price}`;
};